<template>
  <v-dialog :value="open" max-width="1100" persistent content-class="presupuesto-show-dialog-content" @input="$emit('close')">
    <v-card v-if="presupuesto" class="presupuesto-show-card">

      <v-card-title>
        Presupuesto
        <v-spacer />
        <v-btn icon @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="presupuesto-show-content pb-2">

        <!-- ── INFO PANEL ── -->
        <div class="show-info-panel mb-3">
          <v-row dense>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-pound</v-icon>Número</div>
                <div class="info-value">{{ presupuesto.numeroPresupuesto || presupuesto.id }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-account</v-icon>Cliente</div>
                <div class="info-value">{{ presupuesto.cliente ? presupuesto.cliente.razonSocial : "—" }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-account-tie</v-icon>Vendedor</div>
                <div class="info-value">{{ vendedorDisplay(presupuesto) }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-warehouse</v-icon>Depósito</div>
                <div class="info-value">{{ presupuesto.deposito ? presupuesto.deposito.nombre : "—" }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-calendar</v-icon>Fecha</div>
                <div class="info-value">{{ formatFecha(presupuesto.fecha || presupuesto.createdAt) }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label"><v-icon x-small class="mr-1">mdi-format-list-numbered</v-icon>Lista de precios</div>
                <div class="info-value">{{ presupuesto.listaPreciosVentaNombre || "—" }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="info-field">
                <div class="info-label">Estado</div>
                <div class="mt-1">
                  <v-chip small dark class="white--text" :color="colorChipEstadoPresupuesto(presupuesto.estado)">{{ labelEstadoPresupuesto(presupuesto.estado) }}</v-chip>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- ── TABLA ── -->
        <div class="table-fixed-header-wrap">
          <v-data-table
            class="mt-2 movimiento-show-table"
            fixed-header
            height="380"
            :headers="headersItems"
            :items="presupuesto.items || []"
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.nombre="{ item }">{{ getProductoNombre(item) }}</template>
            <template v-slot:item.marca="{ item }">{{ item.productoMarca || "—" }}</template>
            <template v-slot:item.modelo="{ item }">{{ item.productoModelo || "—" }}</template>
            <template v-slot:item.tamanio="{ item }">{{ item.productoTamanio || "—" }}</template>
            <template v-slot:item.precioUnitario="{ item }">
              <span class="d-inline-flex align-center">
                ${{ getPrecioUnitarioOriginal(item).toFixed(2) }}
                <v-tooltip v-if="item.precioManual" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" x-small color="orange darken-2" class="ml-1">mdi-pencil</v-icon>
                  </template>
                  <span>{{ $t('common.precioManualTooltip') }}</span>
                </v-tooltip>
              </span>
            </template>
            <template v-slot:item.oferta="{ item }">
              <span v-if="tieneOfertaAplicada(item)" class="success--text font-weight-medium">
                <v-icon x-small>mdi-tag</v-icon> {{ getOfertaDescripcion(item) }}
              </span>
              <span v-else class="grey--text">—</span>
            </template>
            <template v-slot:item.precioTotal="{ item }">
              <div class="font-weight-bold">
                ${{ getTotalLineaSinDescuentoGlobal(item).toFixed(2) }}
              </div>
            </template>
            <template slot="no-data">
              <div class="text-center py-4 grey--text">Sin productos</div>
            </template>
          </v-data-table>
        </div>

      </v-card-text>

      <!-- ── ACCIONES ── -->
      <v-card-actions class="presupuesto-show-actions flex-wrap align-center">
        <template v-if="presupuesto.estado === 'CREADO' || !presupuesto.estado">
          <v-btn text color="primary" :loading="loading" @click="actualizarPrecios">
            Actualizar precios
          </v-btn>
          <v-btn color="success" :loading="loadingConfirmar" @click="confirmar">
            Confirmar (crear orden de venta)
          </v-btn>
        </template>
        <v-spacer />
        <div v-if="presupuesto" class="footer-total">
          <div v-if="presupuesto.descuento > 0" class="footer-total-sub">
            Desc. {{ presupuesto.descuento }}%&nbsp;
            <span class="error--text">−${{ Number(presupuesto.descuentoMonto || 0).toFixed(2) }}</span>
          </div>
          <div v-if="presupuesto.recargo > 0" class="footer-total-sub">
            Rec. {{ presupuesto.recargo }}%&nbsp;
            <span class="warning--text">+${{ Number(presupuesto.recargoMonto || 0).toFixed(2) }}</span>
          </div>
          <div class="footer-total-amount">
            Total&nbsp;&nbsp;<strong>${{ Number(presupuesto.precioTotal || 0).toFixed(2) }}</strong>
          </div>
        </div>
      </v-card-actions>

      <v-snackbar v-model="snackbarError" color="error" :timeout="15000" top>
        <span style="white-space: pre-line;">{{ errorConfirmar }}</span>
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbarError = false">Cerrar</v-btn>
        </template>
      </v-snackbar>
    </v-card>

    <v-card v-else>
      <v-card-text class="text-center pa-8">
        <v-progress-circular indeterminate color="primary" />
        <p class="mt-3 grey--text">Cargando presupuesto...</p>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import PresupuestoProvider from "../../providers/PresupuestoProvider";
import OrdenVentaProvider from "../../providers/OrdenVentaProvider";
import { formatFechaVentas } from "../../utils/dateFormat";
import { labelEstadoPresupuesto, colorChipEstadoPresupuesto } from "../../utils/estadoLabels";

export default {
  name: "PresupuestoShow",
  props: {
    open: { type: Boolean, default: true },
    presupuestoId: { type: String, default: null },
  },
  data() {
    return {
      presupuesto: null,
      loading: false,
      loadingConfirmar: false,
      errorConfirmar: null,
      snackbarError: false,
      headersItems: [
        { text: "Producto", value: "nombre", sortable: false },
        { text: "Marca", value: "marca", sortable: false },
        { text: "Modelo", value: "modelo", sortable: false },
        { text: "Tamaño", value: "tamanio", sortable: false },
        { text: "Cant.", value: "cantidad", sortable: false, width: 60 },
        { text: "P. unitario", value: "precioUnitario", sortable: false },
        { text: "Oferta", value: "oferta", sortable: false },
        { text: "Total línea", value: "precioTotal", sortable: false },
      ],
    };
  },
  mounted() { if (this.open && this.presupuestoId) this.load(); },
  watch: {
    open(v) { if (v && this.presupuestoId) this.load(); else this.presupuesto = null; },
    presupuestoId(v) { if (v && this.open) this.load(); },
  },
  methods: {
    formatFecha: formatFechaVentas,
    labelEstadoPresupuesto,
    colorChipEstadoPresupuesto,
    vendedorDisplay(doc) {
      const v = doc.vendedor || doc.vendedorSnapshot;
      if (!v) return "—";
      return [v.nombre, v.apellido].filter(Boolean).join(" ") || "—";
    },
    getProductoNombre(item) {
      return item.productoNombre || `${item.productoMarca || ""} ${item.productoModelo || ""}`.trim();
    },
    getPrecioUnitarioOriginal(item) {
      if (item.precioBase != null && Number.isFinite(Number(item.precioBase))) return Number(item.precioBase);
      const cant = Number(item.cantidad) || 0;
      if (cant > 0 && item.precioBaseLinea != null) {
        return Math.round((Number(item.precioBaseLinea) / cant) * 100) / 100;
      }
      if (cant > 0 && item.precioConOfertas != null) {
        return Math.round((Number(item.precioConOfertas) / cant) * 100) / 100;
      }
      return Number(item.precioUnitario || 0);
    },
    getTotalLineaSinDescuentoGlobal(item) {
      if (item.precioConOfertas != null) return Number(item.precioConOfertas) || 0;
      if (item.precioBaseLinea != null) return Number(item.precioBaseLinea) || 0;
      const cantidad = Number(item.cantidad) || 0;
      const precioBase = this.getPrecioUnitarioOriginal(item);
      if (cantidad > 0 && precioBase > 0) {
        return Math.round(cantidad * precioBase * 100) / 100;
      }
      return 0;
    },
    tieneOfertaAplicada(item) { return !!(item.ofertaSnapshot && item.ofertaSnapshot.nombre); },
    getOfertaDescripcion(item) {
      if (!this.tieneOfertaAplicada(item)) return "";
      const snap = item.ofertaSnapshot;
      return snap.descuentoAplicadoMonto != null
        ? `${snap.nombre} (-$${Number(snap.descuentoAplicadoMonto).toFixed(2)})`
        : snap.nombre;
    },
    async load() {
      if (!this.presupuestoId) return;
      try {
        const r = await PresupuestoProvider.findPresupuesto(this.presupuestoId);
        this.presupuesto = (r.data && r.data.presupuestoFind) || null;
      } catch (e) { console.error(e); }
    },
    async actualizarPrecios() {
      if (!this.presupuestoId) return;
      this.loading = true;
      try { await PresupuestoProvider.actualizarPreciosPresupuesto(this.presupuestoId); await this.load(); this.$emit("refreshed"); }
      catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    async confirmar() {
      if (!this.presupuestoId) return;
      this.errorConfirmar = null;
      this.loadingConfirmar = true;
      try {
        const r = await OrdenVentaProvider.createOrdenVenta({ presupuestoId: this.presupuestoId });
        const orden = r.data && r.data.ordenVentaCreate;
        if (orden && orden.id) this.$emit("confirmar", orden.id);
      } catch (e) {
        this.errorConfirmar = e?.graphQLErrors?.[0]?.message || e?.message || "Error al crear la orden de venta.";
        this.snackbarError = true;
      } finally { this.loadingConfirmar = false; }
    },
  },
};
</script>

<style scoped>
::v-deep .presupuesto-show-dialog-content {
  height: 90vh;
  max-height: 90vh;
}
.presupuesto-show-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.presupuesto-show-content {
  flex: 1 1 auto;
  overflow-y: auto;
}
.presupuesto-show-actions {
  flex: 0 0 auto;
  border-top: 1px solid #e0e0e0;
}
.table-fixed-header-wrap { min-height: 0; }
.movimiento-show-table { border-radius: 8px; overflow: hidden; }

/* Info panel */
.show-info-panel {
  background: #f8f9fc;
  border-radius: 8px;
  border: 1px solid #e8eaf0;
  padding: 12px 16px;
}
.info-field { padding: 2px 0; }
.info-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #90a4ae;
  font-weight: 600;
  display: flex;
  align-items: center;
}
.info-value {
  font-size: 0.92rem;
  font-weight: 500;
  color: #37474f;
  margin-top: 2px;
}

/* Footer total */
.footer-total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.3;
}
.footer-total-sub { font-size: 0.85rem; color: #90a4ae; }
.footer-total-amount { font-size: 1.25rem; color: #2e7d32; font-weight: 600; }

::v-deep .v-chip { color: #fff !important; }
::v-deep .v-chip .v-chip__content { color: #fff !important; }
</style>
